/**
 * The text in the player (handoff §10, §11.2): the body's own runs with every gap
 * drawn from the server's segments, never from its words.
 *
 * Gap looks (ADR-116 Decision 5: every empty gap is one width, whatever its answer):
 * - empty: 56×22, dashed accent underline;
 * - active (filling): a 28 px ring, min 56 wide, showing what is being typed, live;
 * - answered: the text's own word, solid accent underline;
 * - revealed: the word in muted text with a quiet underline. Never red.
 * Every occurrence of a form shares its gap's state, and all of them carry the ring.
 *
 * Not selectable (Stanley's decision for the player): a missed tap opens no Touch to
 * Search and no OS selection.
 */
import type { PracticeGap, PracticeSegment } from '../../../types'

interface Props {
  segments: PracticeSegment[]
  gaps: Map<string, PracticeGap>
  mode: 'reading' | 'filling'
  activeId?: string | null
  /** What is being typed, shown live in the active gap. */
  typed?: string
  onTapGap?: (id: string) => void
  fontSize: number
  lineHeight: number
  /** The active gap's ring: 56×28 on a phone, 64×30 on desktop (handoff §11.2, §19.8). */
  activeSize?: { minWidth: number; height: number }
}

export default function PlayerText({ segments, gaps, mode, activeId, typed = '', onTapGap, fontSize, lineHeight, activeSize = { minWidth: 56, height: 28 } }: Props) {
  const seen = new Map<string, number>()
  return (
    <p
      data-testid="player-text"
      style={{
        margin: 0, fontFamily: 'var(--font-content)', fontSize, lineHeight: `${lineHeight}px`, color: 'var(--text-primary)',
        whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
      }}
    >
      {segments.map((s, i) => {
        if (s.gap_id === null) return <span key={i}>{s.text}</span>
        const gap = gaps.get(s.gap_id)
        if (!gap) return null
        const nth = seen.get(gap.id) ?? 0
        seen.set(gap.id, nth + 1)
        const active = mode === 'filling' && gap.id === activeId
        const word = gap.written[nth] ?? gap.answer ?? ''
        const common = {
          'data-gap': gap.id,
          ...(nth === 0 ? { 'data-gap-first': gap.id } : {}),
          onClick: onTapGap ? () => onTapGap(gap.id) : undefined,
        }
        if (active && gap.state === 'empty') {
          return (
            <span
              key={i}
              {...common}
              data-testid="player-gap-active"
              style={{
                display: 'inline-block', minWidth: activeSize.minWidth, height: activeSize.height, boxSizing: 'border-box', margin: '0 2px',
                padding: '0 6px', verticalAlign: 'middle', lineHeight: `${activeSize.height - 4}px`, borderRadius: 6,
                border: '2px solid var(--accent)', background: 'var(--accent-soft)', color: 'var(--accent)',
                fontWeight: 600, whiteSpace: 'pre', cursor: 'pointer',
              }}
            >
              {typed || ' '}
            </span>
          )
        }
        if (gap.state === 'empty') {
          return (
            <span
              key={i}
              {...common}
              data-testid="player-gap-empty"
              aria-label={undefined}
              style={{
                display: 'inline-block', width: 56, height: 22, margin: '0 2px', verticalAlign: 'baseline',
                borderBottom: '2px dashed var(--accent)', cursor: onTapGap ? 'pointer' : undefined,
              }}
            />
          )
        }
        const revealed = gap.state === 'revealed'
        return (
          <span
            key={i}
            {...common}
            data-testid={revealed ? 'player-gap-revealed' : 'player-gap-answered'}
            style={{
              textDecorationLine: 'underline', textDecorationStyle: 'solid', textDecorationThickness: 2,
              textUnderlineOffset: 5, cursor: onTapGap ? 'pointer' : undefined,
              textDecorationColor: revealed ? 'var(--text-tertiary)' : 'var(--accent)',
              color: revealed ? 'var(--text-secondary)' : 'var(--text-primary)',
              ...(active ? { outline: '2px solid var(--border)', outlineOffset: 2, borderRadius: 4 } : {}),
            }}
          >
            {word}
          </span>
        )
      })}
    </p>
  )
}
